import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { usePlatformAccessLevel } from '../platformPermissions/usePlatformAccessLevel';
import {
  createContextChange,
  deleteContextChange,
  fetchContextChanges,
  fetchSourceRiskLinks,
  promoteContextChange,
  updateContextChange,
} from './contextApi';
import type { ContextChange, ContextSource, ContextTrend } from './types';
import { ContextChangeFormModal } from './ContextChangeFormModal';

const SOURCE_LABEL: Record<ContextSource, string> = {
  interno: 'Interno',
  externo: 'Externo',
};

const TREND_LABEL: Record<ContextTrend, string> = {
  positivo: 'Positivo',
  negativo: 'Negativo',
  neutro: 'Neutro',
};

interface ContextChangesTabProps {
  canEdit: boolean;
}

export function ContextChangesTab({ canEdit }: ContextChangesTabProps) {
  const { confirm } = useConfirmDialog();
  const navigate = useNavigate();
  const { canEdit: canEditRisks } = usePlatformAccessLevel('risks.access');
  const [changes, setChanges] = useState<ContextChange[]>([]);
  const [promotedByContext, setPromotedByContext] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ContextChange | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, links] = await Promise.all([
        fetchContextChanges(showInactive ? {} : { active: true }),
        fetchSourceRiskLinks(),
      ]);
      setChanges(data);
      setPromotedByContext(links.byContextId);
    } catch {
      setError('No se pudieron cargar los cambios de contexto.');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (payload: Parameters<typeof createContextChange>[0]) => {
    if (editing) {
      await updateContextChange(editing.id, payload);
    } else {
      await createContextChange(payload);
    }
    await load();
  };

  const handleDelete = async (change: ContextChange) => {
    const ok = await confirm({
      title: 'Eliminar cambio',
      message: '¿Eliminar este cambio de contexto?',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteContextChange(change.id);
      await load();
    } catch {
      window.alert('No se pudo eliminar: puede tener riesgos vinculados.');
    }
  };

  const handlePromote = async (change: ContextChange) => {
    setPromotingId(change.id);
    try {
      const risk = await promoteContextChange(change.id);
      setPromotedByContext((prev) => ({ ...prev, [change.id]: risk.id }));
      navigate(`/riesgos/${risk.id}`);
    } catch {
      window.alert('No se pudo promover a la matriz de riesgos.');
    } finally {
      setPromotingId(null);
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando cambios de contexto…</p>;
  }

  if (error) {
    return <p className='text-sm text-destructive'>{error}</p>;
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <label className='flex items-center gap-2 text-sm'>
          <input
            type='checkbox'
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Mostrar inactivos
        </label>
        {canEdit && (
          <button
            type='button'
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground'
          >
            Nuevo cambio de contexto
          </button>
        )}
      </div>

      <div className='overflow-x-auto rounded-lg border border-border'>
        <table className='min-w-full text-sm'>
          <thead className='bg-muted'>
            <tr>
              <th className='px-4 py-2 text-left font-medium'>Origen</th>
              <th className='px-4 py-2 text-left font-medium'>Situación</th>
              <th className='px-4 py-2 text-left font-medium'>Efecto</th>
              <th className='px-4 py-2 text-left font-medium'>Tendencia</th>
              <th className='px-4 py-2 text-left font-medium'>Estado</th>
              {(canEdit || canEditRisks) && (
                <th className='px-4 py-2 text-left font-medium'>Acciones</th>
              )}
            </tr>
          </thead>
          <tbody>
            {changes.length === 0 && (
              <tr>
                <td
                  colSpan={canEdit || canEditRisks ? 6 : 5}
                  className='px-4 py-6 text-center text-muted-foreground'
                >
                  Sin cambios de contexto registrados
                </td>
              </tr>
            )}
            {changes.map((change) => {
              const promotedRiskId = promotedByContext[change.id];
              return (
                <tr key={change.id} className='border-t border-border'>
                  <td className='px-4 py-2'>{SOURCE_LABEL[change.source]}</td>
                  <td className='px-4 py-2 max-w-xs truncate' title={change.situation}>
                    {change.situation}
                  </td>
                  <td className='px-4 py-2 max-w-xs truncate' title={change.effect}>
                    {change.effect}
                  </td>
                  <td className='px-4 py-2'>{TREND_LABEL[change.trend]}</td>
                  <td className='px-4 py-2'>
                    <span
                      className={
                        change.active
                          ? 'text-green-700 bg-green-100 px-2 py-0.5 rounded text-xs'
                          : 'text-zinc-600 bg-zinc-200 px-2 py-0.5 rounded text-xs'
                      }
                    >
                      {change.active ? 'Activo' : 'Inactivo'}
                    </span>
                    {promotedRiskId && (
                      <Link
                        to={`/riesgos/${promotedRiskId}`}
                        className='ml-2 text-xs text-primary hover:underline'
                      >
                        Promovido
                      </Link>
                    )}
                  </td>
                  {(canEdit || canEditRisks) && (
                    <td className='px-4 py-2 space-x-2'>
                      {canEdit && (
                        <>
                          <button
                            type='button'
                            onClick={() => {
                              setEditing(change);
                              setModalOpen(true);
                            }}
                            className='text-primary hover:underline text-xs'
                          >
                            Editar
                          </button>
                          {change.active && !promotedRiskId && (
                            <button
                              type='button'
                              onClick={() => void handleDelete(change)}
                              className='text-destructive hover:underline text-xs'
                            >
                              Eliminar
                            </button>
                          )}
                        </>
                      )}
                      {canEdit && canEditRisks && change.active && !promotedRiskId && (
                        <button
                          type='button'
                          disabled={promotingId === change.id}
                          onClick={() => void handlePromote(change)}
                          className='text-primary hover:underline text-xs disabled:opacity-50'
                        >
                          {promotingId === change.id ? 'Promoviendo…' : 'Promover'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ContextChangeFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
